import { useForm } from '@tanstack/react-form'
import { isToday, isYesterday } from 'date-fns'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  FINISH_CLASSIFICATION_CATEGORIES,
  FINISH_CLASSIFICATION_CATEGORIES_LEGACY,
  getCategoryDeprecation,
  getCategoryMetadata,
  NOT_APPLICABLE,
  useSubcategories,
} from '~/entities/categories'
import { getConsignmentUrl } from '~/entities/consignment'
import {
  DOCUMENT_SERVER_STATUS,
  getDocumentsUrlWithCurrentParams,
  getIsClassificationFinished,
  PRIOR_AUTH_PHASES,
  ProcessingAlert,
  useClassificationReviews,
  useCurrentDocument,
} from '~/entities/document'
import { FLAG_TYPES, useStepFlagState } from '~/entities/document-flags'
import { useUserStore } from '~/entities/user'
import { useAuditTrail, useAuditTrailQueryParams } from '~/features/audit-trail'
import { useFirstInteraction, useStepCompletion, useStepView } from '~/features/document-analytics'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault, formatTimeDefault } from '~/shared/lib/datetime'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { useUndoRedo } from '~/shared/lib/use-undo-redo'
import {
  Alert,
  Button,
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  IconButton,
  LocalIcon,
  Select,
  Text,
  Textarea,
} from '~/shared/ui'
import { ReviewActions } from '~/widgets/review-actions'
import { useSubmitClassificationNotes } from '../../api'
import { CONFIDENCE_SCORE_THRESHOLD } from '../../config'
import {
  getPossibleOutcomes,
  getPossiblePhases,
  getPossibleReasons,
  getSatisfyingCategory,
  getSatisfyingCategoryWithOutcomePreservation,
  getSatisfyingCategoryWithPreservation,
  getVisibleFacetOptions,
} from '../../lib'
import { useClassification, useSubmitClassificationReview } from '../../model'
import { ClassificationChip } from './classification-chip'
import { ValidateAndFinishDialog } from './validate-and-finish-dialog'

export function Classification() {
  useStepView()
  const formRef = useRef<HTMLDivElement>(null)
  useFirstInteraction(formRef)
  const { trackStepCompletion } = useStepCompletion()
  const {
    data: { document },
  } = useCurrentDocument()
  const subcategories = useSubcategories()
  const llmCategoryMetadata = getCategoryMetadata({
    category: document.category_llm,
    subcategories,
  })
  const classification = useClassification({ documentKey: document.key })
  const [optionalNotes, setOptionalNotes] = useState('')
  const { data: classificationReviews } = useClassificationReviews({ documentKey: document.key })
  const {
    submitClassificationNotes,
    isPending: isSubmitClassificationNotesPending,
    isSuccess: isSubmitClassificationNotesSuccess,
  } = useSubmitClassificationNotes({ documentKey: document.key })

  const { submitClassificationReview, isClassificationReviewError } = useSubmitClassificationReview(
    {
      documentKey: document.key,
      currentReviewStatus: document.review_status,
      openValidateAndFinishDialog: () => setIsValidateAndFinishDialogOpen(true),
    },
  )
  const firstName = useUserStore((state) => state.firstName())
  const isOnshore = useUserStore((state) => state.isOnshore)
  const isConsignmentLocked = !isOnshore && !!document.onshore_only
  const [isValidateAndFinishDialogOpen, setIsValidateAndFinishDialogOpen] = useState(false)
  const [isWarningAlertHiddenManually, setIsWarningAlertHiddenManually] = useState(false)
  const { isAuditTrailVisible } = useAuditTrail()
  const queryParams = useAuditTrailQueryParams()
  const isEobEnabled = useIsFeatureEnabled('2026-02-eob-phase-1')
  const isHideLlmClassificationEnabled = useIsFeatureEnabled('2026-08-hide-llm-classification')
  const isPriorAuthClassification = PRIOR_AUTH_PHASES.includes(document.category_phase ?? '')
  const shouldHideLlmClassification =
    isHideLlmClassificationEnabled &&
    isPriorAuthClassification &&
    !getIsClassificationFinished(document, subcategories)
  const navigate = useNavigate()
  const stepFlag = useStepFlagState(FLAG_TYPES.STEP_CLASSIFICATION)

  const comments = useMemo(() => {
    return classificationReviews.items?.filter((review) => !!review.notes) || []
  }, [classificationReviews])

  const defaultValues = useMemo(
    () => ({
      category: shouldHideLlmClassification ? '' : document.category || '',
      notes: '',
      revertButtonType: 'revert-opti' as 'revert-opti' | 'restore-edits',
    }),
    [document.category, shouldHideLlmClassification],
  )

  const documentCategoryMetadata = useMemo(
    () => getCategoryMetadata({ category: document.category, subcategories }),
    [document.category, subcategories],
  )
  // Anchors the retired options that stay visible in the dropdowns. Deliberately the persisted
  // classification rather than the live field value — anchoring on the live value would make the
  // option disappear as soon as the reviewer picks something else.
  const documentCategoryDeprecation = useMemo(
    () => getCategoryDeprecation({ category: document.category, subcategories }),
    [document.category, subcategories],
  )

  // Track if fields have changed to determine if feedback is mandatory
  const hasFieldsChanged = useCallback(
    (categoryValue: string) => {
      // Moving off a retired category onto its sanctioned replacement is a migration the reviewer
      // was forced into, not a judgement call, so it must not demand a written explanation. Any
      // other destination is a real reclassification and still does.
      if (documentCategoryDeprecation?.supersededBy?.label === categoryValue) {
        return false
      }

      const currentMetadata = getCategoryMetadata({
        category: categoryValue,
        subcategories,
      })

      return (
        currentMetadata?.phase?.label !== documentCategoryMetadata?.phase?.label ||
        currentMetadata?.outcome?.label !== documentCategoryMetadata?.outcome?.label ||
        currentMetadata?.reason?.label !== documentCategoryMetadata?.reason?.label
      )
    },
    [documentCategoryDeprecation, documentCategoryMetadata, subcategories],
  )

  // Blocks completing the review while the resolved classification is retired. Covers the case
  // the disabled options cannot: a document already classified as retired, seeded into the form
  // by `defaultValues` before the reviewer touches anything.
  const isCategoryDeprecated = useCallback(
    (categoryValue: string) => !!getCategoryDeprecation({ category: categoryValue, subcategories }),
    [subcategories],
  )

  const form = useForm({
    defaultValues,
    validators: {
      onChange: ({ value }) => {
        const fields: { category?: string; notes?: string } = {}

        if (isCategoryDeprecated(value.category)) {
          fields.category = 'This classification has been retired. Select a current one.'
        }
        if (
          !shouldHideLlmClassification &&
          hasFieldsChanged(value.category) &&
          !value.notes.trim()
        ) {
          fields.notes =
            'Share why you adjusted this classification — your feedback helps improve accuracy and future suggestions.'
        }

        return Object.keys(fields).length ? { fields } : undefined
      },
    },
    onSubmit: async ({ value }) => {
      await submitClassificationReview({
        document_category: value.category,
        ...(value.notes.trim() && { notes: value.notes.trim() }),
      })
      trackStepCompletion()
    },
  })

  const {
    set: setHistoryValues,
    undo: undoHistory,
    redo: redoHistory,
    canUndo,
    canRedo,
  } = useUndoRedo({
    initialValues: defaultValues,
    setFieldValue: form.setFieldValue,
  })

  const validate = useCallback(() => {
    setTimeout(() => {
      form.validate('change')
    }, 0)
  }, [form])

  const undo = useCallback(() => {
    undoHistory()
    validate()
  }, [undoHistory, validate])

  const redo = useCallback(() => {
    redoHistory()
    validate()
  }, [redoHistory, validate])

  const revert = useCallback(() => {
    if (form.state.values.revertButtonType === 'revert-opti') {
      form.setFieldValue('category', document.category_llm ?? '')
      if (document.category_llm !== document.category) {
        form.setFieldValue('revertButtonType', 'restore-edits')
      }
      setHistoryValues({
        category: document.category_llm ?? '',
        revertButtonType:
          document.category_llm !== document.category ? 'restore-edits' : 'revert-opti',
      })
    } else {
      form.setFieldValue('category', document.category ?? '')
      form.setFieldValue('revertButtonType', 'revert-opti')
      setHistoryValues({ category: document.category ?? '', revertButtonType: 'revert-opti' })
    }
    validate()
  }, [document.category, document.category_llm, form, setHistoryValues, validate])

  const { isScrolled, sentinelElement } = useIsScrolled()
  const isWarningVisible =
    !shouldHideLlmClassification &&
    classification.confidenceScore < CONFIDENCE_SCORE_THRESHOLD &&
    !isWarningAlertHiddenManually

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {document.review_status === DOCUMENT_SERVER_STATUS.processing && (
        <ProcessingAlert className={cn({ 'mb-2': !isWarningVisible })} />
      )}
      {isWarningVisible && (
        <Alert
          className="mb-2"
          appearance="WARNING"
          materialIcon="warning"
          action={
            <IconButton
              className="text-alert-warning-message"
              size="medium"
              container="small"
              materialIcon="close"
              onClick={() => setIsWarningAlertHiddenManually(true)}
            />
          }
        >
          opti wasn’t fully confident in the classification. Please double-check before proceeding.
        </Alert>
      )}
      <form.Subscribe
        selector={(state) => state.values.category}
        children={(category) => {
          const deprecation = getCategoryDeprecation({ category, subcategories })

          if (!deprecation) {
            return null
          }

          return (
            <Alert
              className="mb-2"
              appearance="INFO"
              materialIcon="info"
            >
              This document’s classification <strong>{deprecation.displayLabel}</strong> has been
              retired.{' '}
              {deprecation.supersededBy ? (
                <>
                  Select <strong>{deprecation.supersededBy.displayLabel}</strong> or another current
                  option to continue.
                </>
              ) : (
                'Select a current option to continue.'
              )}
            </Alert>
          )
        }}
      />
      <div
        ref={formRef}
        className="grid grid-cols-2 gap-6 px-6 py-4"
      >
        <div className="flex flex-col gap-3">
          {!shouldHideLlmClassification && (
            <div className="flex flex-col gap-2.5">
              <div className="text-text-secondary flex items-center gap-1">
                <LocalIcon
                  name="opti"
                  size="18"
                />
                <Text appearance="SUBTITLE2">opti Suggested Classification</Text>
              </div>
              <form.Subscribe
                selector={(state) => {
                  const selectedCategoryMetadata = getCategoryMetadata({
                    category: state.values.category,
                    subcategories,
                  })
                  return {
                    selectedCategory: state.values.category,
                    selectedCategoryMetadata,
                  }
                }}
                children={({ selectedCategory, selectedCategoryMetadata }) => (
                  <div className="flex flex-wrap gap-2.5">
                    {llmCategoryMetadata ? (
                      <>
                        {!!llmCategoryMetadata.phase && (
                          <ClassificationChip
                            confidenceScore={classification.confidenceScore}
                            displayLabel={llmCategoryMetadata.phase.displayLabel}
                            isSelected={
                              selectedCategoryMetadata?.phase?.label ===
                              llmCategoryMetadata.phase.label
                            }
                          />
                        )}
                        {!!llmCategoryMetadata.outcome && (
                          <ClassificationChip
                            confidenceScore={classification.confidenceScore}
                            displayLabel={llmCategoryMetadata.outcome.displayLabel}
                            isSelected={
                              selectedCategoryMetadata?.outcome?.label ===
                              llmCategoryMetadata.outcome.label
                            }
                          />
                        )}
                        {!!llmCategoryMetadata.reason && (
                          <ClassificationChip
                            confidenceScore={classification.confidenceScore}
                            displayLabel={llmCategoryMetadata.reason.displayLabel}
                            isSelected={
                              selectedCategoryMetadata?.reason?.label ===
                              llmCategoryMetadata.reason.label
                            }
                          />
                        )}
                      </>
                    ) : (
                      <ClassificationChip
                        confidenceScore={classification.confidenceScore}
                        displayLabel={document.category_llm || 'Unknown category'}
                        isSelected={selectedCategory === document.category_llm}
                      />
                    )}
                  </div>
                )}
              />
            </div>
          )}
          <div className="flex flex-col gap-3">
            {comments.length === 0 ? (
              <Text
                className="py-[5px]"
                appearance="OVERLINE"
              >
                Comments
              </Text>
            ) : (
              <CollapsibleRoot defaultOpen={false}>
                <CollapsibleTrigger
                  label="Comments"
                  counter={comments.length}
                />
                <CollapsibleContent>
                  <div className="mt-3 flex flex-col gap-3">
                    {comments.map((comment) => {
                      const date = comment.time_created
                        ? new Date(comment.time_created)
                        : new Date()
                      return (
                        <div
                          key={comment.time_created}
                          className="flex flex-col py-1"
                        >
                          <div className="flex gap-1">
                            <div className="flex w-full items-center justify-between gap-1">
                              <div className="flex items-start gap-1">
                                <div className="text-text-secondary flex items-center gap-2 whitespace-nowrap">
                                  <Text
                                    className="uppercase"
                                    appearance="CAPTION"
                                  >
                                    {formatTimeDefault(comment.time_created ?? 0)}
                                  </Text>
                                  <span className="bg-text-secondary h-1 w-1 rounded-full" />
                                  <Text
                                    className="uppercase"
                                    appearance="CAPTION"
                                    title={
                                      isToday(date) || isYesterday(date)
                                        ? formatDateDefault(date)
                                        : undefined
                                    }
                                  >
                                    {isToday(date)
                                      ? 'Today'
                                      : isYesterday(date)
                                        ? 'Yesterday'
                                        : formatDateDefault(date)}
                                  </Text>
                                  <LocalIcon
                                    name="personOutlined"
                                    size="16"
                                  />
                                </div>
                                <Text appearance="CAPTION">
                                  {comment.user?.first_name} {comment.user?.last_name}
                                </Text>
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-col gap-1">
                            {comment.document_category ? (
                              <div className="flex items-start gap-2">
                                <LocalIcon
                                  className="text-alert-info-message mt-[3px] shrink-0"
                                  name="attachNoteOutlined"
                                  size="16"
                                />
                                <Text
                                  className="text-alert-info-message"
                                  appearance="BODY2"
                                >
                                  Reason for change
                                </Text>
                              </div>
                            ) : null}
                            <Text appearance="CAPTION">{comment.notes}</Text>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CollapsibleContent>
              </CollapsibleRoot>
            )}

            {isSubmitClassificationNotesSuccess ? (
              <div className="border-alert-info-message text-chip-status-info-content flex h-[85px] items-center justify-center rounded-lg border">
                <Text appearance="CAPTION">Thanks! Your feedback makes opti smarter.</Text>
              </div>
            ) : (
              <div className="relative">
                <Textarea
                  placeholder="Want to tell us more?"
                  className="h-[85px] resize-none"
                  value={optionalNotes}
                  onChange={(e) => setOptionalNotes(e.target.value)}
                />
                <Button
                  className="absolute right-3 bottom-2"
                  size="small"
                  disabled={optionalNotes.length < 3 || isSubmitClassificationNotesPending}
                  onClick={() => {
                    submitClassificationNotes({
                      notes: optionalNotes.trim(),
                    })
                  }}
                >
                  Send
                </Button>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="text-text-secondary flex items-center gap-1">
            <LocalIcon
              name="checkDoc"
              size="18"
            />
            <Text appearance="SUBTITLE2">Validate Classification</Text>
          </div>
          <div className="mt-2 flex flex-col gap-4">
            <form.Field
              name="category"
              children={(field) => {
                const metadata = getCategoryMetadata({
                  category: field.state.value,
                  subcategories,
                })
                const effectivePhase =
                  metadata?.phase?.label ||
                  (shouldHideLlmClassification ? llmCategoryMetadata?.phase?.label : '') ||
                  ''
                // Dead-end options are dropped, except the one the document itself carries, which
                // stays rendered but disabled so a retired classification is still legible.
                const possiblePhases = getVisibleFacetOptions({
                  options: getPossiblePhases({ subcategories }),
                  anchorLabel: documentCategoryMetadata?.phase?.label,
                })
                const possibleOutcomes = getVisibleFacetOptions({
                  options: getPossibleOutcomes({
                    phase: effectivePhase,
                    subcategories,
                  }),
                  anchorLabel: documentCategoryMetadata?.outcome?.label,
                })
                const possibleReasons = getVisibleFacetOptions({
                  options: getPossibleReasons({
                    phase: effectivePhase,
                    outcome: metadata?.outcome?.label ?? undefined,
                    subcategories,
                  }),
                  anchorLabel: documentCategoryMetadata?.reason?.label,
                })
                const sortedPhases = shouldHideLlmClassification
                  ? [...possiblePhases].sort((a, b) => a.displayLabel.localeCompare(b.displayLabel))
                  : possiblePhases
                const sortedOutcomes = shouldHideLlmClassification
                  ? [...possibleOutcomes].sort((a, b) =>
                      a.displayLabel.localeCompare(b.displayLabel),
                    )
                  : possibleOutcomes
                const sortedReasons = shouldHideLlmClassification
                  ? [...possibleReasons].sort((a, b) =>
                      a.displayLabel.localeCompare(b.displayLabel),
                    )
                  : possibleReasons
                return (
                  <>
                    <Select
                      label="Phase"
                      name="phase"
                      value={effectivePhase}
                      onValueChange={(value) => {
                        const satisfyingCategory = getSatisfyingCategoryWithPreservation({
                          newPhase: value as string,
                          currentOutcome: metadata?.outcome?.label,
                          currentReason: metadata?.reason?.label,
                          subcategories,
                        })
                        field.handleChange(satisfyingCategory)
                        setHistoryValues({ category: satisfyingCategory })
                      }}
                      onBlur={field.handleBlur}
                      help={
                        !shouldHideLlmClassification &&
                        classification.confidenceScore < CONFIDENCE_SCORE_THRESHOLD &&
                        llmCategoryMetadata?.phase?.label === metadata?.phase?.label
                      }
                    >
                      {sortedPhases.map((phase) => (
                        <Select.Item
                          key={phase.label}
                          value={phase.label}
                          disabled={phase.isDeprecated}
                        >
                          {phase.displayLabel}
                        </Select.Item>
                      ))}
                    </Select>
                    <Select
                      label="Outcome"
                      name="outcome"
                      value={
                        shouldHideLlmClassification && !metadata
                          ? ''
                          : (metadata?.outcome?.label ?? NOT_APPLICABLE)
                      }
                      onValueChange={(value) => {
                        const satisfyingCategory = getSatisfyingCategoryWithOutcomePreservation({
                          currentPhase: effectivePhase,
                          newOutcome: value as string,
                          currentReason: metadata?.reason?.label,
                          subcategories,
                        })
                        field.handleChange(satisfyingCategory)
                        setHistoryValues({ category: satisfyingCategory })
                      }}
                      onBlur={field.handleBlur}
                      help={
                        !shouldHideLlmClassification &&
                        classification.confidenceScore < CONFIDENCE_SCORE_THRESHOLD &&
                        llmCategoryMetadata?.outcome?.label === metadata?.outcome?.label
                      }
                    >
                      {sortedOutcomes.map((outcome) => (
                        <Select.Item
                          key={outcome.label}
                          value={outcome.label}
                          disabled={outcome.isDeprecated}
                        >
                          {outcome.displayLabel}
                        </Select.Item>
                      ))}
                    </Select>
                    <Select
                      label="Reason"
                      name="reason"
                      value={
                        shouldHideLlmClassification && !metadata
                          ? ''
                          : (metadata?.reason?.label ?? NOT_APPLICABLE)
                      }
                      onValueChange={(value) => {
                        const satisfyingCategory = getSatisfyingCategory({
                          phase: effectivePhase,
                          outcome: metadata?.outcome?.label ?? undefined,
                          reason: value as string,
                          subcategories,
                        })
                        field.handleChange(satisfyingCategory)
                        setHistoryValues({ category: satisfyingCategory })
                      }}
                      onBlur={field.handleBlur}
                      help={
                        !shouldHideLlmClassification &&
                        classification.confidenceScore < CONFIDENCE_SCORE_THRESHOLD &&
                        llmCategoryMetadata?.reason?.label === metadata?.reason?.label
                      }
                    >
                      {sortedReasons.map((reason) => (
                        <Select.Item
                          key={reason.label}
                          value={reason.label}
                          disabled={reason.isDeprecated}
                        >
                          {reason.displayLabel}
                        </Select.Item>
                      ))}
                    </Select>
                  </>
                )
              }}
            />
          </div>
          <form.Subscribe
            selector={(state) => ({
              values: state.values,
            })}
            children={({ values }) => {
              if (shouldHideLlmClassification || !hasFieldsChanged(values.category)) {
                return null
              }
              return (
                <div className="mt-4 flex flex-col gap-2">
                  <Text appearance="CAPTION">
                    You've modified the classification from the original suggestion. Please explain
                    why <span className="text-error-main">*</span>
                  </Text>
                  <form.Field
                    name="notes"
                    children={(field) => {
                      return (
                        <div className="relative mb-9">
                          <Textarea
                            className="h-[64px] resize-none"
                            placeholder="Add a short note here to explain your choice"
                            filled
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={(e) => {
                              field.handleBlur()
                              setHistoryValues({ notes: e.target.value })
                            }}
                          />
                        </div>
                      )
                    }}
                  />
                </div>
              )
            }}
          />
        </div>
      </div>
      {isValidateAndFinishDialogOpen && (
        <ValidateAndFinishDialog
          username={String(firstName || '')}
          onClose={() => setIsValidateAndFinishDialogOpen(false)}
        />
      )}
      <form.Subscribe
        selector={(state) => ({
          isDirty: state.isDirty,
          isSubmitting: state.isSubmitting,
          isValid: state.isValid,
          category: state.values.category,
          revertButtonType: state.values.revertButtonType,
          values: state.values,
          isDefaultValue: state.isDefaultValue,
        })}
        children={({
          isDirty,
          isSubmitting,
          isValid,
          category,
          revertButtonType,
          values,
          isDefaultValue,
        }) => {
          const shouldFinish = (
            isEobEnabled
              ? FINISH_CLASSIFICATION_CATEGORIES
              : FINISH_CLASSIFICATION_CATEGORIES_LEGACY
          ).includes(category ?? '')
          return (
            <ReviewActions
              isDirty={isDirty}
              isSubmitDisabled={
                stepFlag.hasActiveStepFlag
                  ? false
                  : isSubmitting ||
                    !isValid ||
                    (getIsClassificationFinished(document, subcategories) && isDefaultValue)
              }
              onSubmit={
                stepFlag.hasActiveStepFlag
                  ? () => navigate(getDocumentsUrlWithCurrentParams())
                  : form.handleSubmit
              }
              submitLabel={
                stepFlag.hasActiveStepFlag
                  ? 'Return to Work Queue'
                  : shouldFinish
                    ? 'Validate & Finish'
                    : 'Validate'
              }
              submitTooltip={
                stepFlag.hasActiveStepFlag
                  ? undefined
                  : isCategoryDeprecated(values.category)
                    ? 'Select a current classification to validate.'
                    : !shouldHideLlmClassification &&
                        hasFieldsChanged(values.category) &&
                        !values.notes.trim()
                      ? 'Enter a brief explanation to validate.'
                      : undefined
              }
              canUndo={canUndo}
              canRedo={canRedo}
              revertButtonType={revertButtonType}
              onRevert={shouldHideLlmClassification ? undefined : revert}
              revertLabel={shouldFinish && isAuditTrailVisible ? 'Revert' : undefined}
              revertForceDisabled={
                shouldHideLlmClassification
                  ? undefined
                  : revertButtonType === 'revert-opti'
                    ? category === document.category_llm
                    : undefined
              }
              onUndo={undo}
              onRedo={redo}
              previousStepUrl={
                isConsignmentLocked
                  ? undefined
                  : getConsignmentUrl(document.cns_key, { queryParams })
              }
              previousStepLabel="Go to clustering"
              isError={isClassificationReviewError}
              errorContent="opti couldn’t complete the classification due to a system error. Please try again."
              isScrolled={isScrolled}
            />
          )
        }}
      />
      {sentinelElement}
    </form>
  )
}
